/**
 * 디자인 PPT 한 장 — 1280×720 HTML 을 샌드박스 iframe(스크립트 금지)에 그리고 칸 너비에 맞춰 줄인다.
 * editable 이면 글자 조각만 바로 고칠 수 있게 하고, 고친 조각 HTML 을 onEdit 로 올린다.
 * arrange 이면 요소를 고르고 옮기며, 글 상자는 필요할 때만 열어 고친다 — 다 고치면 onEdit(html, { before, done: true }).
 */

import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  ARRANGE_CSS,
  ARRANGE_STYLE_ID,
  PICK_ATTR,
  arrangeChain,
  clearPick,
  markPick,
  readTranslate,
  setTranslate,
  shiftLayer,
} from './freeDesignArrange';

export const SLIDE_W = 1280;
export const SLIDE_H = 720;

const EDIT_STYLE_ID = 'wqfd-edit-style';
const EDIT_CSS = `
[data-wqfd-edit]{cursor:text;border-radius:4px;transition:box-shadow .12s}
[data-wqfd-edit]:hover{box-shadow:0 0 0 2px rgba(49,130,246,.45)}
[data-wqfd-edit]:focus{outline:none;box-shadow:0 0 0 3px #3182F6}
`;
const SKIP_TAGS = new Set(['STYLE', 'SVG', 'svg', 'SCRIPT', 'TEMPLATE']);

export const buildSlideDoc = (head, html) => (
  `<!doctype html><html lang="ko"><head>${head || '<meta charset="utf-8">'}</head>`
  + `<body><div data-slide-root>${html || ''}</div></body></html>`
);

const hasOwnText = (el) => Array.from(el.childNodes).some(
  (node) => node.nodeType === 3 && node.textContent.trim().length > 0,
);

const insideSkipped = (el, root) => {
  for (let cur = el; cur && cur !== root; cur = cur.parentElement) {
    if (SKIP_TAGS.has(cur.tagName) || cur.hasAttribute('data-icon') || cur.namespaceURI === 'http://www.w3.org/2000/svg') return true;
  }
  return false;
};

const editableTargets = (root) => {
  const picked = [];
  root.querySelectorAll('*').forEach((el) => {
    if (!hasOwnText(el) || insideSkipped(el, root)) return;
    if (picked.some((p) => p.contains(el))) return;
    picked.push(el);
  });
  return picked;
};

// Chrome 은 plaintext-only 편집 칸을 white-space:pre-wrap 으로 바꿔, HTML 소스의 줄바꿈·들여쓰기가 그대로 보이며
// 칸이 커지고 겹친다. 편집을 켜기 전에 보이는 모양 그대로 공백을 접어 둔다.
export const collapseEditableWhitespace = (el) => {
  const win = el.ownerDocument.defaultView;
  const collapses = (node) => !['pre', 'pre-wrap', 'pre-line', 'break-spaces'].includes(win.getComputedStyle(node).whiteSpace);
  const isBlock = (node) => !['inline', 'inline-block', 'contents'].includes(win.getComputedStyle(node).display);
  let prev = null;
  let atLineStart = true;
  const endLine = () => {
    if (prev && prev.data.endsWith(' ')) prev.data = prev.data.slice(0, -1);
    prev = null;
    atLineStart = true;
  };
  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        if (!collapses(child.parentElement)) { prev = null; atLineStart = false; return; }
        let text = child.data.replace(/[ \t\n\r\f]+/g, ' ');
        if (atLineStart || (prev && prev.data.endsWith(' '))) text = text.replace(/^ /, '');
        if (text !== child.data) child.data = text;
        if (text) { prev = child; atLineStart = false; }
        return;
      }
      if (child.nodeType !== 1) return;
      if (child.tagName === 'BR') { endLine(); return; }
      const block = isBlock(child);
      if (block) endLine();
      walk(child);
      if (block) endLine();
    });
  };
  walk(el);
  endLine();
};

/** 편집 표시를 걷어낸 조각 HTML (서버도 contenteditable 은 지운다). */
export const cleanFragment = (root) => {
  const clone = root.cloneNode(true);
  clone.querySelectorAll('[data-wqfd-edit]').forEach((el) => {
    el.removeAttribute('data-wqfd-edit');
    el.removeAttribute('contenteditable');
    el.removeAttribute('spellcheck');
  });
  clone.querySelectorAll(`[${PICK_ATTR}]`).forEach((el) => el.removeAttribute(PICK_ATTR));
  [EDIT_STYLE_ID, ARRANGE_STYLE_ID].forEach((id) => clone.querySelector(`#${id}`)?.remove());
  return clone.innerHTML.trim();
};

const makeEditable = (el) => {
  if (!el.hasAttribute('data-wqfd-edit')) collapseEditableWhitespace(el);
  el.setAttribute('data-wqfd-edit', '');
  el.setAttribute('spellcheck', 'false');
  try {
    el.contentEditable = 'plaintext-only';
  } catch (_error) {
    el.contentEditable = 'true';
  }
  if (el.contentEditable !== 'plaintext-only') el.contentEditable = 'true';
};

const unmakeEditable = (el) => {
  el.removeAttribute('data-wqfd-edit');
  el.removeAttribute('contenteditable');
  el.removeAttribute('spellcheck');
};

// plaintext-only 를 모르는 브라우저에서도 서식 없이 붙인다.
const pastePlain = (doc, event) => {
  const text = event.clipboardData?.getData('text/plain');
  if (text == null) return;
  event.preventDefault();
  doc.execCommand('insertText', false, text.replace(/\s*\n\s*/g, ' '));
};

const placeCaret = (doc, el, x, y) => {
  let range = null;
  if (typeof doc.caretRangeFromPoint === 'function') range = doc.caretRangeFromPoint(x, y);
  else if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(x, y);
    if (pos) {
      range = doc.createRange();
      range.setStart(pos.offsetNode, pos.offset);
    }
  }
  if (!range || !el.contains(range.startContainer)) {
    range = doc.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
  }
  const sel = doc.getSelection?.();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
};

const overflowing = (root) => {
  const box = root.getBoundingClientRect();
  return editableTargets(root).some((el) => {
    const r = el.getBoundingClientRect();
    return el.scrollHeight > el.clientHeight + 4 || r.right > box.right + 2 || r.bottom > box.bottom + 2;
  });
};

const NUDGE_SAVE_MS = 400;
const DRAG_START_PX = 3;

const FreeDesignSlideFrame = forwardRef(({
  head,
  html,
  title,
  editable = false,
  arrange = false,
  onEdit,
  onArrange,
  onPickChange,
  onEditingChange,
  onUndoRequest,
  onOverflowChange,
  className = '',
  testId,
}, ref) => {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);
  const shownRef = useRef(html);
  const onEditRef = useRef(onEdit);
  const onArrangeRef = useRef(onArrange);
  const onPickRef = useRef(onPickChange);
  const onEditingRef = useRef(onEditingChange);
  const onUndoRef = useRef(onUndoRequest);
  const onOverflowRef = useRef(onOverflowChange);
  const pickedRef = useRef(null);
  const [scale, setScale] = useState(0);
  const [loadTick, setLoadTick] = useState(0);
  onEditRef.current = onEdit;
  onArrangeRef.current = onArrange;
  onPickRef.current = onPickChange;
  onEditingRef.current = onEditingChange;
  onUndoRef.current = onUndoRequest;
  onOverflowRef.current = onOverflowChange;

  const rootOf = () => frameRef.current?.contentDocument?.querySelector('[data-slide-root]') || null;

  const pick = useCallback((el) => {
    const root = rootOf();
    pickedRef.current = el || null;
    if (root) markPick(root, el || null);
    onPickRef.current?.(Boolean(el));
  }, []);

  const emitArrange = useCallback((action) => {
    const root = rootOf();
    if (!root) return;
    const next = cleanFragment(root);
    shownRef.current = next;
    onArrangeRef.current?.(next, action);
    onOverflowRef.current?.(overflowing(root));
  }, []);

  // 옮기기 화면에서 글 상자 하나만 잠깐 글자 칸으로 연다 — { el, before(열기 전 조각), timer }
  const editingRef = useRef(null);

  const emitEdit = useCallback((meta) => {
    const root = rootOf();
    if (!root) return;
    const next = cleanFragment(root);
    shownRef.current = next;
    onEditRef.current?.(next, meta);
    onOverflowRef.current?.(overflowing(root));
  }, []);

  const endEdit = useCallback(() => {
    const cur = editingRef.current;
    if (!cur) return;
    editingRef.current = null;
    clearTimeout(cur.timer);
    if (cur.el.ownerDocument.activeElement === cur.el) cur.el.blur();
    unmakeEditable(cur.el);
    if (cur.el.ownerDocument === frameRef.current?.contentDocument) emitEdit({ before: cur.before, done: true });
    onEditingRef.current?.(false);
  }, [emitEdit]);

  const beginEdit = useCallback((el, x, y) => {
    if (editingRef.current?.el === el) return;
    endEdit();
    const root = rootOf();
    if (!root || !root.contains(el)) return;
    const before = cleanFragment(root);
    pick(el);
    makeEditable(el);
    editingRef.current = { el, before, timer: null };
    onEditingRef.current?.(true);
    el.focus();
    placeCaret(el.ownerDocument, el, x, y);
  }, [endEdit, pick]);

  useImperativeHandle(ref, () => ({
    removePicked: () => {
      const el = pickedRef.current;
      if (!el?.isConnected) return;
      endEdit();
      el.remove();
      pick(null);
      emitArrange('delete');
    },
    shiftPicked: (direction) => {
      const el = pickedRef.current;
      if (!el?.isConnected) return;
      endEdit();
      shiftLayer(el, direction);
      emitArrange('order');
    },
    clearPicked: () => {
      endEdit();
      pick(null);
    },
  }), [emitArrange, endEdit, pick]);

  // 내가 방금 올린 글자로 다시 그리면 커서가 튄다 — 지금 문서가 보여 주는 조각과 다른 HTML 일 때만 새로 만든다.
  // 되돌리기는 처음 그린 조각과 같은 HTML 로 돌아올 수 있는데, 그사이 문서는 제자리에서 바뀌었으므로 강제로 다시 그린다.
  const docHtmlRef = useRef(html);
  const docNonceRef = useRef(0);
  if (html !== shownRef.current) {
    if (html === docHtmlRef.current) docNonceRef.current += 1;
    docHtmlRef.current = html;
    shownRef.current = html;
  }
  const docHtml = docHtmlRef.current;
  const docNonce = docNonceRef.current;
  const srcDoc = useMemo(
    () => buildSlideDoc(head, docHtml) + (docNonce ? `<!--r${docNonce}-->` : ''),
    [head, docHtml, docNonce],
  );

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setScale(el.clientWidth / SLIDE_W);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleLoad = useCallback(() => setLoadTick((n) => n + 1), []);

  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    const root = doc?.querySelector('[data-slide-root]');
    if (!doc || !root) return undefined;
    if (!editable || arrange) {
      doc.getElementById(EDIT_STYLE_ID)?.remove();
      root.querySelectorAll('[data-wqfd-edit]').forEach((el) => {
        if (el !== editingRef.current?.el) unmakeEditable(el);
      });
      return undefined;
    }
    if (!doc.getElementById(EDIT_STYLE_ID)) {
      const style = doc.createElement('style');
      style.id = EDIT_STYLE_ID;
      style.textContent = EDIT_CSS;
      doc.head.appendChild(style);
    }
    editableTargets(root).forEach(makeEditable);
    let timer = null;
    const emit = () => {
      const next = cleanFragment(root);
      shownRef.current = next;
      onEditRef.current?.(next);
      onOverflowRef.current?.(overflowing(root));
    };
    const onInput = () => {
      clearTimeout(timer);
      timer = setTimeout(emit, 250);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        event.target?.blur?.();
      }
      if (event.key === 'Escape') event.target?.blur?.();
    };
    const onPaste = (event) => pastePlain(doc, event);
    const onBlur = () => {
      clearTimeout(timer);
      emit();
    };
    root.addEventListener('input', onInput);
    root.addEventListener('keydown', onKeyDown);
    root.addEventListener('paste', onPaste);
    root.addEventListener('focusout', onBlur);
    return () => {
      clearTimeout(timer);
      root.removeEventListener('input', onInput);
      root.removeEventListener('keydown', onKeyDown);
      root.removeEventListener('paste', onPaste);
      root.removeEventListener('focusout', onBlur);
    };
  }, [editable, arrange, loadTick]);

  // ── 데스크톱 고치기 (파워포인트처럼) ──
  // 한 번 누르면 가장 바깥 묶음(카드)을 고르고, 고른 것을 다시 누르면 한 겹 안으로 들어간다.
  // 글 상자를 고른 채 다시 누르거나 두 번 누르면 글자를 고친다. 고른 것은 끌어서·화살표로 옮기고 Delete 로 지운다.
  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    const root = doc?.querySelector('[data-slide-root]');
    if (!doc || !root) return undefined;
    if (!arrange) {
      doc.getElementById(ARRANGE_STYLE_ID)?.remove();
      clearPick(root);
      pickedRef.current = null;
      return undefined;
    }
    if (!doc.getElementById(ARRANGE_STYLE_ID)) {
      const style = doc.createElement('style');
      style.id = ARRANGE_STYLE_ID;
      style.textContent = ARRANGE_CSS;
      doc.head.appendChild(style);
    }
    // 문서를 새로 그렸으면(되돌리기 등) 예전 고른 요소는 없다.
    if (!pickedRef.current?.isConnected || !root.contains(pickedRef.current)) pick(null);
    let drag = null;
    let nudgeTimer = null;
    const textBoxOf = (el) => editableTargets(root).find((t) => t.contains(el)) || null;
    const editingEl = () => editingRef.current?.el || null;
    const onPointerDown = (event) => {
      if (event.button !== 0) return;
      const editing = editingEl();
      if (editing?.contains(event.target)) return;
      if (editing) endEdit();
      const node = event.target?.nodeType === 1 ? event.target : event.target?.parentElement;
      const chain = arrangeChain(node, root, editableTargets(root));
      const at = chain.indexOf(pickedRef.current);
      const target = at >= 0 ? pickedRef.current : chain[chain.length - 1] || null;
      if (target !== pickedRef.current) pick(target);
      if (!target) return;
      event.preventDefault();
      frameRef.current?.focus?.();
      const textBox = textBoxOf(node);
      drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        base: readTranslate(target),
        el: target,
        moved: false,
        // 이미 고른 것을 그냥 눌렀다 떼면: 한 겹 안으로, 맨 안쪽 글 상자면 글자 고치기
        inner: at > 0 ? chain[at - 1] : null,
        edit: at === 0 && textBox === target ? target : null,
      };
      try { target.setPointerCapture?.(event.pointerId); } catch (_error) { /* 캡처가 안 돼도 문서 안에서는 따라간다 */ }
    };
    const onPointerMove = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.moved && Math.abs(dx) < DRAG_START_PX && Math.abs(dy) < DRAG_START_PX) return;
      drag.moved = true;
      setTranslate(drag.el, drag.base.x + dx, drag.base.y + dy);
    };
    const onPointerUp = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { moved, inner, edit } = drag;
      drag = null;
      if (moved) emitArrange('move');
      else if (event.type === 'pointercancel') return;
      else if (inner) pick(inner);
      else if (edit) beginEdit(edit, event.clientX, event.clientY);
    };
    const onDoubleClick = (event) => {
      if (editingEl()) return;
      const node = event.target?.nodeType === 1 ? event.target : event.target?.parentElement;
      const textBox = node && textBoxOf(node);
      if (textBox) beginEdit(textBox, event.clientX, event.clientY);
    };
    const onInput = () => {
      const cur = editingRef.current;
      if (!cur) return;
      clearTimeout(cur.timer);
      cur.timer = setTimeout(() => emitEdit(), 250);
    };
    const onFocusOut = (event) => {
      if (event.target === editingEl()) endEdit();
    };
    const onPaste = (event) => {
      if (editingEl()) pastePlain(doc, event);
    };
    const onKeyDown = (event) => {
      const editing = editingEl();
      if (editing) {
        if (event.key === 'Escape' || (event.key === 'Enter' && !event.shiftKey && !event.isComposing)) {
          event.preventDefault();
          endEdit();
        }
        return;
      }
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        onUndoRef.current?.();
        return;
      }
      const el = pickedRef.current;
      if (!el?.isConnected) return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        el.remove();
        pick(null);
        emitArrange('delete');
        return;
      }
      if (event.key === 'Escape') {
        pick(null);
        return;
      }
      const step = event.shiftKey ? 10 : 1;
      const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[event.key];
      if (!delta) return;
      event.preventDefault();
      const now = readTranslate(el);
      setTranslate(el, now.x + delta[0], now.y + delta[1]);
      clearTimeout(nudgeTimer);
      nudgeTimer = setTimeout(() => emitArrange('move'), NUDGE_SAVE_MS);
    };
    doc.addEventListener('pointerdown', onPointerDown);
    doc.addEventListener('pointermove', onPointerMove);
    doc.addEventListener('pointerup', onPointerUp);
    doc.addEventListener('pointercancel', onPointerUp);
    doc.addEventListener('keydown', onKeyDown);
    doc.addEventListener('dblclick', onDoubleClick);
    root.addEventListener('input', onInput);
    root.addEventListener('focusout', onFocusOut);
    root.addEventListener('paste', onPaste);
    return () => {
      endEdit();
      const live = rootOf() === root;
      if (nudgeTimer) {
        clearTimeout(nudgeTimer);
        if (live) emitArrange('move');
      }
      doc.removeEventListener('pointerdown', onPointerDown);
      doc.removeEventListener('pointermove', onPointerMove);
      doc.removeEventListener('pointerup', onPointerUp);
      doc.removeEventListener('pointercancel', onPointerUp);
      doc.removeEventListener('keydown', onKeyDown);
      doc.removeEventListener('dblclick', onDoubleClick);
      root.removeEventListener('input', onInput);
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('paste', onPaste);
    };
  }, [arrange, loadTick, emitArrange, pick, beginEdit, endEdit, emitEdit]);

  return (
    <div
      className={`wqfd-frame ${className}`.trim()}
      data-testid={testId}
      ref={wrapRef}
      style={{ height: scale ? SLIDE_H * scale : undefined }}
    >
      <iframe
        className="wqfd-frame__iframe"
        onLoad={handleLoad}
        ref={frameRef}
        sandbox="allow-same-origin"
        srcDoc={srcDoc}
        style={{
          transform: `scale(${scale || 0.0001})`,
          width: SLIDE_W,
          height: SLIDE_H,
          pointerEvents: editable || arrange ? 'auto' : 'none',
        }}
        tabIndex={editable || arrange ? 0 : -1}
        title={title || '슬라이드'}
      />
    </div>
  );
});

FreeDesignSlideFrame.displayName = 'FreeDesignSlideFrame';

export default FreeDesignSlideFrame;
